import type { CrashBreadcrumb, CrashEvent, CrashIdentityData, CrashSnapshot } from "./types.js";

export interface PendingExitInfo
{
  handled: boolean;
  reason: string | null;
}

export interface NativeCrashConfig
{
  endpoint: string | null;
  key_present: boolean;
  native_build_id: string | null;
  version: string | null;
  version_code: number | null;
  pending: number;
}

type CrashlyticsPlugin = {
  configure( options: { build_id: string; env: string } ): Promise<NativeCrashConfig>;
  capture( options: { event: string } ): Promise<{ stored: boolean }>;
  setIdentity( options: { user_id: string | null; device_id: string | null; session_id: string | null; analytics_id: string | null; consent: boolean } ): Promise<void>;
  checkpoint( options: { name: string; at: string } ): Promise<void>;
  snapshot( options: { snapshot: string } ): Promise<void>;
  breadcrumbs( options: { breadcrumbs: string } ): Promise<void>;
  getPendingExit(): Promise<PendingExitInfo>;
  flush(): Promise<{ sent: number; pending: number }>;
  pending(): Promise<{ pending: number; files: string[] }>;
  debugCrash( options: { kind: 'java' | 'native' | 'anr' | 'renderer' | 'oom' } ): Promise<void>;
};

function plugin(): CrashlyticsPlugin | null
{
  const cap = ( window as any ).Capacitor;
  const platform = cap?.getPlatform?.();
  if ( platform !== 'android' && platform !== 'ios' ) return null;
  return ( cap.Plugins?.Crashlytics as CrashlyticsPlugin | undefined ) ?? null;
}

export class NativeBridge
{
  public static isAvailable(): boolean
  {
    return plugin() != null;
  }

  public static async configure( build_id: string, env: string ): Promise<NativeCrashConfig | null>
  {
    const p = plugin();
    if ( !p ) return null;
    try { return await p.configure( { build_id, env } ); }
    catch ( e ) { console.warn( '[Crash] native configure failed', e ); return null; }
  }

  public static async capture( event: CrashEvent ): Promise<boolean>
  {
    const p = plugin();
    if ( !p ) return false;
    try
    {
      const result = await p.capture( { event: JSON.stringify( event ) } );
      return !!result?.stored;
    }
    catch ( e )
    {
      console.warn( '[Crash] native capture failed', e );
      return false;
    }
  }

  public static setIdentity( data: CrashIdentityData, consent: boolean ): void
  {
    const p = plugin();
    if ( !p ) return;
    void p.setIdentity( { ...data, consent } ).catch( () => {} );
  }

  public static checkpoint( name: string, at: string ): void
  {
    const p = plugin();
    if ( !p ) return;
    void p.checkpoint( { name, at } ).catch( () => {} );
  }

  public static snapshot( snapshot: CrashSnapshot ): void
  {
    const p = plugin();
    if ( !p ) return;
    void p.snapshot( { snapshot: JSON.stringify( snapshot ) } ).catch( () => {} );
  }

  public static breadcrumbs( list: CrashBreadcrumb[] ): void
  {
    const p = plugin();
    if ( !p ) return;
    void p.breadcrumbs( { breadcrumbs: JSON.stringify( list ) } ).catch( () => {} );
  }

  public static async getPendingExit(): Promise<PendingExitInfo | null>
  {
    const p = plugin();
    if ( !p ) return null;
    try { return await p.getPendingExit(); }
    catch { return null; }
  }

  public static async flush(): Promise<{ sent: number; pending: number } | null>
  {
    const p = plugin();
    if ( !p ) return null;
    try { return await p.flush(); }
    catch { return null; }
  }

  public static async pending(): Promise<{ pending: number; files: string[] } | null>
  {
    const p = plugin();
    if ( !p ) return null;
    try { return await p.pending(); }
    catch { return null; }
  }

  public static async debugCrash( kind: 'java' | 'native' | 'anr' | 'renderer' | 'oom' ): Promise<void>
  {
    const p = plugin();
    if ( !p )
    {
      console.warn( `[Crash] debugCrash(${kind}) is only available in native builds` );
      return;
    }
    await p.debugCrash( { kind } );
  }
}
